import { Component, OnInit } from '@angular/core';
import { Location } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { IPageDataViewModel } from 'src/app/share/pages/pages';
import { Observable, of, merge } from 'rxjs';
import { delay, map, switchMap, catchError } from 'rxjs/operators';
import { CommonService } from 'src/app/share/services/common.service';
import { DateUtil } from 'src/app/share/utils/date-util';
import { FormInputComponent, IFormInputViewModel } from 'src/app/share/forms/form-input/form-input.component';
import { FormSelectComponent, IFormSelectChangedArg, IFormSelectViewModel } from 'src/app/share/forms/form-select/form-select.component';
import { IDisplaySelectViewModel } from 'src/app/share/forms/form-select/form-select.component';
import { ClockInOutActionType, ClockInOutBreakAction, IBreak } from '../payroll-break/payroll-break.component';

@Component({
  selector: 'app-break-edit',
  templateUrl: './break-edit.component.html',
  styleUrls: ['./break-edit.component.scss']
})
export class BreakEditComponent implements OnInit {

  constructor(private commonService: CommonService, protected route: ActivatedRoute, private location: Location) { }

  vm$: Observable<IViewModelTimeclock>;

  ngOnInit(): void {
    const breakId: number = Number(this.route.snapshot.paramMap.get("breakId"));
    const payrollDate = this.route.snapshot.queryParamMap.get('payrollDate') ?? undefined;
    const dayIdStr = this.route.snapshot.queryParamMap.get('dayId');
    const dayId = dayIdStr !== null ? Number(dayIdStr) : undefined;
    this.vm$ = this.getDataForEdit$(breakId, payrollDate, dayId);
  }

  private getDataForEdit$(breakId: number, payrollDate: string, dayId: number): Observable<IViewModelTimeclock> {
    const initial$ = of({
      workingText: 'Loading Data...',
      payrollBreakVm: {} as IPayrollBreakViewModel, // Add default payrollBreakVm to conform to IViewModelTimeclock
      validInput: false
    });

    const loader$ = this.commonService.getData('PageCommand', 'home', 'prepare-break-for-edit', { breakId: breakId }, '/api/ChauffeurPortal').pipe(
      map((v: IViewModelTimeclock) => {
        if (v.workingText)
          return { ...v };

        const ret = { ...v, actionsAllowed: true };
        if (ret.data?.payrollBreak) {
          ret.payrollBreakVm = {
            data: {
              id: 0,
              breakType: 0,
              startAt: { date: '', time: '' },
              endAt: { date: '', time: '' },
              editStatus: ''
            },
            payrollPeriodDriverDayBreakId: ret.data.payrollBreak.payrollPeriodDriverDayBreakId,
            payrollPeriodDriverDayId: (ret.data.payrollBreak.payrollPeriodDriverDayId ?? 0) > 0 
                    ? ret.data.payrollBreak.payrollPeriodDriverDayId : dayId,
            payrollDate: payrollDate
          };
          const parsed = JSON.parse(ret.data.payrollBreak.breakJson);
          if (Array.isArray(parsed) && parsed.length > 0) {
            ret.payrollBreakVm.data = parsed[0];
            ret.validInput = true, 
            ret.payrollBreakVm.payrollDate = ret.payrollBreakVm.data.startAt.date
          }
        }

        ret.breakFormVm = ret?.payrollBreakVm?.data ? this.buildInputViewModel(ret.payrollBreakVm.data) : undefined
        ret.breakId = breakId;

        return ret;
      })
    );
    return merge(initial$, loader$);
  }

  refreshPage() {
    const breakId: number = Number(this.route.snapshot.paramMap.get("breakId"));
    const payrollDate = this.route.snapshot.queryParamMap.get('payrollDate') ?? undefined;
    const dayIdStr = this.route.snapshot.queryParamMap.get('dayId');
    const dayId = dayIdStr !== null ? Number(dayIdStr) : undefined;
    this.vm$ = this.getDataForEdit$(breakId, payrollDate, dayId);
  }

  buildInputViewModel(breakDay: ITimeclockData): IBreakEntryFormViewModel {

    const data: IBreakEntryFormData = {
      breakTimeStart: breakDay?.startAt?.time ? breakDay.startAt.time : '',
      breakTimeEnd: breakDay?.endAt?.time ? breakDay.endAt.time : ''
    };

    const ret: IBreakEntryFormViewModel = {
      data: data,
      breakTimeStartVm: FormInputComponent.viewModel(data, 'breakTimeStart', { title: 'Break Time Start', isRequired: true, type: 'time' }),
      breakTimeEndVm: FormInputComponent.viewModel(data, 'breakTimeEnd', { title: 'Break Time End', isRequired: true, type: 'time' })
    };
    return ret;
  }

  breakTimeChanged(vm: IViewModelTimeclock) {
    vm.validInput = vm.breakFormVm.data.breakTimeStart.length > 0 && vm.breakFormVm.data.breakTimeEnd.length ? true : false;
  }

  async deleteBreakClicked(vm: IViewModelTimeclock) {
    vm.updateMessage = undefined;
    vm.errorMessage = undefined;
    const actionType = 'delete';

    const breakAction: ClockInOutBreakAction = {
      [actionType]: {
        id: vm.breakId,
        reason: `deleted at ${DateUtil.now()}`,
        at: DateUtil.shortDateTime(DateUtil.now())
      }
    };

    const breakDataItems = await of({}).pipe(
      delay(100),
      switchMap(() => this.executeAction$(vm, 'delete-break', { id: vm.payrollBreakVm.payrollPeriodDriverDayId, dataJson: JSON.stringify(breakAction) })),
    ).toPromise();

    if ((breakDataItems?.data?.status ?? 'error') == 'ok' && vm.errorMessage == undefined)
      this.goBack();
  }

  async updateBreakClicked(vm: IViewModelTimeclock) {
    vm.updateMessage = undefined;
    vm.errorMessage = undefined;
    const actionType: ClockInOutActionType = vm.breakId <= 0 ? 'add' : 'edit';

    const breakAction: ClockInOutBreakAction = {
      [actionType]: {
        breakType: 2,
        at: DateUtil.shortDateTime(DateUtil.buildDateFromDateTimeString(vm.payrollBreakVm.payrollDate, vm.breakFormVm.data?.breakTimeStart)),
        endAt: DateUtil.shortDateTime(DateUtil.getEndDateTime(
              vm.payrollBreakVm.payrollDate, vm.breakFormVm.data?.breakTimeStart, vm.breakFormVm.data?.breakTimeEnd))
      }
    };
    if (actionType === 'edit') {
      breakAction[actionType].id = vm.breakId;
      breakAction[actionType].reason = `edited at ${DateUtil.now()}`;
    }

    console.log('updateBreakClicked', breakAction);

    const breakDataItems = await of({}).pipe(
      delay(100),
      switchMap(() => this.executeAction$(vm, 'update-break', { id: vm.payrollBreakVm.payrollPeriodDriverDayId, dataJson: JSON.stringify(breakAction) }))
    ).toPromise();

    if ((breakDataItems?.data?.status ?? 'error') == 'ok' && vm.errorMessage == undefined)
      this.goBack();
  }

  private executeAction$(vm: IViewModelTimeclock, actionName: string, paramData: any): Observable<IViewModelTimeclock> {
    return this.commonService.getData('PageCommand', 'home', actionName, paramData, '/api/ChauffeurPortal').pipe(
      map((r: IViewModelTimeclock) => {
        if (r.workingText)
          return { ...r, actionsAllowed: false };

        const ret: IViewModelTimeclock = { ...r, actionsAllowed: true };

        if (ret.data && ret.data.status == 'ok') {
          switch (actionName) {
            case 'update-break':
              vm.updateMessage = 'Break updated successfully';
              break;
            case 'delete-break':
              vm.updateMessage = 'Break deleted successfully';
              break;
          }
        } else {
          switch (actionName) {
            case 'update-break':
              vm.errorMessage = ret.errorText ?? 'Error updating break';
              break;
            case 'delete-break':
              vm.errorMessage = ret.errorText ?? 'Error deliting break';
              break;
          }
        }
        return ret;
      }),
      catchError((e, v) => {
        const ret = { ...e };

        console.log('error', e);
        vm.errorMessage = e.errorText ?? 'Error executing action';

        return of(ret);
      })
    );
  }

  goBack() {
    this.location.back();
  }
}

/**
 * 
 */
interface IViewModelTimeclock extends IPageDataViewModel {
  data?: {
    payrollBreak?: { breakJson: string, payrollPeriodDriverDayBreakId: number, payrollPeriodDriverDayId: number },
    status?: string;
  };

  payrollBreakVm: IPayrollBreakViewModel;
  breakFormVm?: IBreakEntryFormViewModel;

  breakId?: number;
  validInput: boolean;

  updateMessage?: string;
  errorMessage?: string;
}

/**
 * 
 */
interface IPayrollBreakViewModel {
  data?: ITimeclockData;
  payrollPeriodDriverDayBreakId: number;
  payrollPeriodDriverDayId: number;
  payrollDate: string;
}

/**
 * 
 */
interface IBreakEntryFormViewModel {
  data?: IBreakEntryFormData;
  breakTimeStartVm: IFormInputViewModel;
  breakTimeEndVm: IFormInputViewModel;
}

/**
 * 
 */
interface IBreakEntryFormData {
  breakTimeStart: string;
  breakTimeEnd: string;
}

type BreakSourceType = 'restBefore' | 'restAfter' | 'restWindow' | 'mealBefore' | 'mealAfter' | 'mealWindow';

interface IFoundBreak {
  break: IBreak;
  source: BreakSourceType;
  windowBlock?: number;
}

export interface IAtInfo {
  date: string;
  time: string;
}

export interface ITimeclockData {
  id: number;
  breakType: number;
  startAt: IAtInfo;
  endAt: IAtInfo;
  editStatus: string;
}